import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  Product,
  AddToCartEvent,
  LOW_STOCK_THRESHOLD,
  VARIANT_LABELS,
  PREPURCHASE_SOLD_OUT_MESSAGE,
  getPrepurchaseStock,
  isPrepurchaseSoldOut,
} from '../../../../shared/models/product.model';
import { RouterLink } from '@angular/router';
import { Minus, Plus, ShoppingCart, Bell, CalendarClock, LucideIconData } from 'lucide-angular';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { VariantBadgeComponent } from '../../../../shared/ui/variant-badge/variant-badge.component';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { ToastService } from '../../../../shared/services/toast.service';
import {
  formatPrice,
  formatOriginalPrice,
  calcDiscountPercent,
  displayLanguage,
  formatCondition,
  getConditionTone,
  formatPowerToughness,
  isSingleProduct,
} from '../../../../shared/utils/product.utils';
import { deriveProductLeafCategoryLink } from '../../../catalog/data/catalog-mappers';

@Component({
  selector: 'app-featured-product-block',
  imports: [RouterLink, ButtonComponent, IconComponent, BadgeComponent, VariantBadgeComponent, TooltipDirective],
  templateUrl: './featured-product-block.component.html',
  styleUrl: './featured-product-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeaturedProductBlockComponent {
  product = input.required<Product>();

  zoomOpen = output<Product>();
  addToCart = output<AddToCartEvent>();
  notifyRequest = output<Product>();
  /** Precompra con cupos: «Reservar» abre el modal de precompra (D25). */
  reserveClick = output<Product>();

  private readonly toastService = inject(ToastService);

  protected quantity = signal(1);

  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly cartIcon: LucideIconData = ShoppingCart;
  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly calendarIcon: LucideIconData = CalendarClock;
  protected readonly prepurchaseSoldOutMessage = PREPURCHASE_SOLD_OUT_MESSAGE;

  protected readonly isSingle = computed(() => isSingleProduct(this.product()));

  /**
   * Misma regla que product-card (D25): una precompra puede tener `stock` 0
   * (aún no existe físicamente); su disponibilidad son los cupos.
   */
  protected isOutOfStock = computed(() => {
    const p = this.product();
    if (p.isPreSale) return getPrepurchaseStock(p) <= 0;
    return p.stock === 0;
  });

  /** Precompra con cupos a 0: «Reservar» se sustituye por «Precompra Agotada». */
  protected isPrepurchaseSoldOut = computed(() => isPrepurchaseSoldOut(this.product()));

  protected isLowStock = computed(() => {
    const stock = this.product().stock;
    return stock > 0 && stock <= LOW_STOCK_THRESHOLD;
  });

  protected discountPercent = computed(() => calcDiscountPercent(this.product()));

  protected formattedPrice = computed(() => formatPrice(this.product()));

  protected formattedOriginalPrice = computed(() => formatOriginalPrice(this.product()));

  protected formattedLanguage = computed(() => displayLanguage(this.product()));

  protected conditionLabel = computed(() => formatCondition(this.product().condition));
  protected conditionTone = computed(() => getConditionTone(this.product().condition));

  protected variantLabel = computed(() => VARIANT_LABELS[this.product().variant]);

  /** Power + toughness combinados ("5/5" MTG · "2000" OP). */
  protected powerToughness = computed(() =>
    formatPowerToughness(this.product().power, this.product().toughness),
  );

  protected powerToughnessLabel = computed(() =>
    this.product().toughness ? 'P/T' : 'Poder',
  );

  /** Link a la subcategoría hoja (set/expansión) — para hacer clicable `setName`. */
  protected setLink = computed<string | null>(() =>
    deriveProductLeafCategoryLink(this.product()),
  );

  protected canDecrement = computed(() => this.quantity() > 1);
  protected canIncrement = computed(() => this.quantity() < this.product().stock);

  protected increment(): void {
    if (this.canIncrement()) {
      this.quantity.update(q => q + 1);
    }
  }

  protected decrement(): void {
    if (this.canDecrement()) {
      this.quantity.update(q => q - 1);
    }
  }

  protected onAddToCart(): void {
    this.addToCart.emit({ product: this.product(), quantity: this.quantity() });
  }

  protected onReserve(): void {
    this.reserveClick.emit(this.product());
  }

  protected onPrepurchaseSoldOutClick(): void {
    this.toastService.show({ type: 'info', message: PREPURCHASE_SOLD_OUT_MESSAGE });
  }

  protected onNotify(): void {
    this.notifyRequest.emit(this.product());
  }

  protected onZoomOpen(): void {
    this.zoomOpen.emit(this.product());
  }
}
